/**
 * PaymentsPanel — Slice 2 (Feb 2026) payment tracking widget mounted on the
 * Lead detail page for any WON or COMPLETED lead.
 *
 * Responsibilities:
 *  - Show the payment schedule (auto-generated at Won-time, editable here).
 *  - Show received payments with mode + reference + date.
 *  - Let the user record a new payment (against a specific stage) with
 *    amount / mode / reference / received-date / notes.
 *  - Let the user defer a pending stage with a new expected date + reason.
 *  - Let NSM+/VP+/President managers delete payments (corrections).
 *
 * The panel is self-contained: it fetches from a single
 * `/leads/{id}/payment-schedule` endpoint that returns billed/paid/outstanding
 * + schedule + payments in one round-trip.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  CurrencyInr, Calendar, Warning, CheckCircle, Plus, Trash, Clock, PencilSimple, SlidersHorizontal,
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import PaymentTermsPicker, { emptyPaymentTerms, totalPercent, isStandardTerms } from './PaymentTermsPicker';
import PaymentTermsTimeline from './PaymentTermsTimeline';

import { confirm } from './ConfirmDialog';
import { notify } from '../lib/notify';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const StageStatusPill = ({ status, dueDate }) => {
  const today = new Date().toISOString().slice(0, 10);
  const overdue = status !== 'received' && dueDate && dueDate.slice(0, 10) < today;
  if (status === 'received') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700"><CheckCircle size={10} weight="bold" />Received</span>;
  if (status === 'deferred') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700"><Clock size={10} weight="bold" />Deferred</span>;
  if (overdue) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-rose-100 text-rose-700"><Warning size={10} weight="bold" />Overdue</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-sky-100 text-sky-700">Pending</span>;
};

export default function PaymentsPanel({ leadId, currentUserRole, onStatusChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modes, setModes] = useState([]);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payStage, setPayStage] = useState(null);
  const [showDeferModal, setShowDeferModal] = useState(false);
  const [deferStage, setDeferStage] = useState(null);
  const [showScheduleEdit, setShowScheduleEdit] = useState(false);
  // iter132.1 — request-a-terms-change modal (goes through VP approval).
  const [showTermsEdit, setShowTermsEdit] = useState(false);

  const canDelete = ['National Sales Manager', 'Vice President', 'President'].includes(currentUserRole);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/leads/${leadId}/payment-schedule`);
      setData(r.data);
      onStatusChange?.(r.data.status);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); axios.get(`${API}/payments/modes`).then(r => setModes(r.data.modes || [])); }, [leadId]); // eslint-disable-line

  const deletePayment = async (paymentId) => {
    if (!await confirm({ message: 'Delete this payment record? The lead status may revert to Won if this drops the total below billed value.' })) return;
    try {
      await axios.delete(`${API}/leads/${leadId}/payments/${paymentId}`);
      await load();
    } catch (e) {
      notify(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <div className="medical-card p-5 mb-6"><p className="text-sm text-[#6D8E87]">Loading payment schedule…</p></div>;
  if (!data) return null;

  const pctPaid = data.billed_value > 0 ? Math.max(0, Math.min(100, (data.paid_amount / data.billed_value) * 100)) : 0;
  // Trust paid_amount vs billed_value as the ground truth — `status ===
  // 'completed'` can briefly lag after a delete during the reload race.
  const fullyPaid = data.billed_value > 0 && data.paid_amount >= data.billed_value - 0.01;

  return (
    <>
    <div className="medical-card p-5 mb-6" data-testid="payments-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-[#052A23] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <CurrencyInr size={20} /> Payment Tracking
          </h3>
          <p className="text-xs text-[#6D8E87] mt-0.5">
            {fullyPaid
              ? 'Fully paid — lead auto-promoted to Completed.'
              : 'Realized revenue only counts once 100% of billed value is collected.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.pending_payment_terms_approval_id ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800" data-testid="terms-edit-pending-pill">
              <Clock size={12} weight="bold" /> Terms edit pending VP
            </span>
          ) : (
            <button
              onClick={() => setShowTermsEdit(true)}
              className="text-xs text-[#00a69c] hover:text-[#008f86] flex items-center gap-1"
              data-testid="request-terms-change-btn"
              title="Request a change to the structured payment terms (routes through VP approval)"
            >
              <SlidersHorizontal size={14} /> Request Terms Change
            </button>
          )}
          <button
            onClick={() => setShowScheduleEdit(true)}
            className="text-xs text-[#00a69c] hover:text-[#008f86] flex items-center gap-1"
            data-testid="edit-schedule-btn"
            title="Edit schedule stages"
          >
            <PencilSimple size={14} /> Edit Schedule
          </button>
        </div>
      </div>
      {data.payment_terms_summary && (
        <div className="mb-4 -mt-2 text-[11px] text-[#6D8E87]" data-testid="current-terms-summary">
          Current terms: <span className="font-semibold text-[#3B5A54]">{data.payment_terms_summary}</span>
        </div>
      )}

      {/* Progress meter — Booked (billed) vs Realized (paid) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="p-3 rounded-lg bg-[#F4F9F7] border border-[#D5E8E4]">
          <p className="text-[10px] uppercase tracking-wide text-[#6D8E87]">Billed</p>
          <p className="text-lg font-bold text-[#052A23]" data-testid="billed-value">{money(data.billed_value)}</p>
        </div>
        <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
          <p className="text-[10px] uppercase tracking-wide text-emerald-700">Realized (paid)</p>
          <p className="text-lg font-bold text-emerald-700" data-testid="paid-value">{money(data.paid_amount)}</p>
        </div>
        <div className={`p-3 rounded-lg border ${data.outstanding > 0 ? 'bg-rose-50 border-rose-200' : 'bg-white border-[#D5E8E4]'}`}>
          <p className="text-[10px] uppercase tracking-wide text-[#6D8E87]">Outstanding</p>
          <p className={`text-lg font-bold ${data.outstanding > 0 ? 'text-rose-700' : 'text-[#3B5A54]'}`} data-testid="outstanding-value">{money(data.outstanding)}</p>
        </div>
      </div>
      <div className="w-full h-2 bg-[#F0F5F3] rounded-full overflow-hidden mb-6">
        <div className={`h-full transition-all ${fullyPaid ? 'bg-emerald-500' : 'bg-[#00a69c]'}`} style={{ width: `${pctPaid}%` }} data-testid="paid-progress-bar" />
      </div>

      {/* Schedule stages */}
      <h4 className="text-sm font-semibold text-[#3B5A54] mb-2">Schedule</h4>
      <div className="space-y-2 mb-6">
        {(data.schedule || []).map(stg => (
          <div key={stg.id} className="p-3 border border-[#D5E8E4] rounded-lg" data-testid={`stage-${stg.id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-[#052A23]">{stg.label}</span>
                <StageStatusPill status={stg.status} dueDate={stg.due_date} />
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold">{money(stg.amount)}</span>
                {stg.status !== 'received' && (
                  <>
                    <button
                      onClick={() => { setPayStage(stg); setShowPayModal(true); }}
                      className="px-2 py-1 rounded bg-emerald-600 text-white text-[11px] hover:bg-emerald-700"
                      data-testid={`record-payment-${stg.id}`}
                    >
                      Record Payment
                    </button>
                    <button
                      onClick={() => { setDeferStage(stg); setShowDeferModal(true); }}
                      className="px-2 py-1 rounded border border-amber-300 text-amber-700 text-[11px] hover:bg-amber-50"
                      data-testid={`defer-stage-${stg.id}`}
                    >
                      Defer
                    </button>
                  </>
                )}
              </div>
            </div>
            <p className="text-[11px] text-[#6D8E87] flex items-center gap-1">
              <Calendar size={11} /> Due {new Date(stg.due_date).toLocaleDateString('en-IN')}
              {stg.paid_amount > 0 && <span className="text-emerald-700"> · Received {money(stg.paid_amount)}</span>}
            </p>
            {(stg.deferrals || []).length > 0 && (
              <div className="mt-2 text-[10px] text-amber-800 bg-amber-50 p-2 rounded border border-amber-200">
                {stg.deferrals.map((d, i) => (
                  <div key={i}>
                    <strong>Deferred to {new Date(d.new_due_date).toLocaleDateString('en-IN')}</strong> · {d.reason}
                    <span className="ml-1 text-amber-600">— {d.deferred_by_name}, {new Date(d.deferred_at).toLocaleDateString('en-IN')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Payments received */}
      <h4 className="text-sm font-semibold text-[#3B5A54] mb-2 flex items-center justify-between">
        Received Payments
        {!fullyPaid && (
          <button
            onClick={() => { setPayStage(null); setShowPayModal(true); }}
            className="text-xs text-[#00a69c] hover:text-[#008f86] flex items-center gap-1"
            data-testid="add-payment-btn"
          >
            <Plus size={14} /> Add Payment
          </button>
        )}
      </h4>
      {(data.payments || []).length === 0 ? (
        <p className="text-xs text-[#6D8E87] italic">No payments received yet.</p>
      ) : (
        <div className="space-y-1">
          {data.payments.map(p => (
            <div key={p.id} className="flex items-center justify-between p-2 border border-[#F0F5F3] rounded text-sm" data-testid={`payment-${p.id}`}>
              <div>
                <span className="font-semibold text-[#052A23]">{money(p.amount)}</span>
                <span className="text-[#6D8E87]"> · {p.mode}</span>
                {p.reference && <span className="text-[#6D8E87]"> · Ref: {p.reference}</span>}
              </div>
              <div className="flex items-center gap-2 text-xs text-[#6D8E87]">
                <span>{new Date(p.received_at).toLocaleDateString('en-IN')}</span>
                {canDelete && (
                  <button
                    onClick={() => deletePayment(p.id)}
                    className="text-rose-500 hover:text-rose-700"
                    title="Delete payment"
                    data-testid={`delete-payment-${p.id}`}
                  >
                    <Trash size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showPayModal && (
        <PaymentModal
          leadId={leadId}
          stage={payStage}
          schedule={data.schedule}
          modes={modes}
          onClose={() => setShowPayModal(false)}
          onSaved={async () => { setShowPayModal(false); await load(); }}
        />
      )}
      {showDeferModal && (
        <DeferModal
          leadId={leadId}
          stage={deferStage}
          onClose={() => setShowDeferModal(false)}
          onSaved={async () => { setShowDeferModal(false); await load(); }}
        />
      )}
      {showScheduleEdit && (
        <ScheduleEditModal
          leadId={leadId}
          initialStages={data.schedule || []}
          billedValue={data.billed_value}
          onClose={() => setShowScheduleEdit(false)}
          onSaved={async () => { setShowScheduleEdit(false); await load(); }}
        />
      )}
      {showTermsEdit && (
        <TermsEditRequestModal
          leadId={leadId}
          currentTerms={data.payment_terms_structured}
          onClose={() => setShowTermsEdit(false)}
          onSaved={async () => { setShowTermsEdit(false); await load(); }}
        />
      )}
    </div>
    <PaymentTermsTimeline history={data.payment_terms_history} />
    </>
  );
}

// iter132.1 — Payment Terms edit-request modal (goes through VP approval).
const TermsEditRequestModal = ({ leadId, currentTerms, onClose, onSaved }) => {
  // Prefill from current structured terms if present; otherwise default.
  const seed = () => {
    const t = currentTerms || {};
    const has = (k) => !!(t[k] && typeof t[k] === 'object');
    if (has('advance_with_po') || has('before_dispatch') || has('on_installation') || has('credit')) {
      return {
        advance_with_po: { enabled: !!t.advance_with_po?.enabled, percent: Number(t.advance_with_po?.percent || 0) },
        before_dispatch: { enabled: !!t.before_dispatch?.enabled, percent: Number(t.before_dispatch?.percent || 0) },
        on_installation: { enabled: !!t.on_installation?.enabled, percent: Number(t.on_installation?.percent || 0) },
        credit: { enabled: !!t.credit?.enabled, percent: Number(t.credit?.percent || 0), days: Number(t.credit?.days || 30) },
      };
    }
    return emptyPaymentTerms();
  };
  const [terms, setTerms] = useState(seed);
  const [saving, setSaving] = useState(false);

  const tot = totalPercent(terms);
  const sumOk = Math.abs(tot - 100) < 0.01;
  const std = isStandardTerms(terms);

  const submit = async () => {
    if (!sumOk) { notify(`Terms must total 100%. Currently ${tot.toFixed(1)}%.`); return; }
    if (terms.credit?.enabled && !(Number(terms.credit?.days) > 0)) {
      notify('Credit term requires a positive number of days.'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/leads/${leadId}/payment-terms/edit-request`, terms);
      notify('Edit request sent to VP. The schedule will change once approved.');
      onSaved?.();
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to submit terms edit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="terms-edit-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#052A23] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            Request Payment Terms Change <span className="text-rose-600 text-lg" aria-hidden="true">*</span>
            <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700" data-testid="terms-edit-required-pill">Required</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6D8E87]">
            Any change to structured payment terms on a Won lead routes through VP for approval. The existing schedule keeps running until VP approves — once approved, the schedule is rebuilt from these new terms while preserving already-received payments.
          </DialogDescription>
        </DialogHeader>
        <PaymentTermsPicker value={terms} onChange={setTerms} />
        {(() => {
          const tone = !sumOk ? 'rose' : std ? 'emerald' : 'amber';
          const bg = { rose: 'bg-rose-50 border-rose-200 text-rose-800',
                       emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                       amber:   'bg-amber-50 border-amber-200 text-amber-800' }[tone];
          const msg = !sumOk
            ? `Terms must total 100%. Currently ${tot.toFixed(1)}%.`
            : std
              ? 'Standard combination — still needs VP approval since terms are being changed post-Won.'
              : 'Non-standard — will require VP approval before applying.';
          return (
            <div className={`mt-3 px-3 py-2 rounded-lg border text-xs ${bg}`} data-testid="terms-edit-status">
              <span className="font-semibold">{tot.toFixed(1)}% total.</span> {msg}
            </div>
          );
        })()}
        <div className="flex gap-3 pt-4">
          {(() => {
            // iter132.5 — Same-source validity flag for the submit gate.
            const anyEnabled = ['advance_with_po', 'before_dispatch', 'on_installation', 'credit'].some(k => terms[k]?.enabled);
            const creditOk = !terms.credit?.enabled || Number(terms.credit?.days) > 0;
            const valid = anyEnabled && sumOk && creditOk;
            let reason = '';
            if (!anyEnabled) reason = 'Select at least one payment term';
            else if (!sumOk) reason = `Terms must total 100% (currently ${tot.toFixed(1)}%)`;
            else if (!creditOk) reason = 'Credit term requires a positive number of days';
            return (
              <>
                <button onClick={onClose} className="flex-1 py-2 border border-[#D5E8E4] rounded-lg hover:bg-gray-50 text-sm" data-testid="terms-edit-cancel">
                  Cancel
                </button>
                <button onClick={submit} disabled={saving || !valid}
                  title={reason}
                  className="flex-1 py-2 bg-emerald-600 text-white rounded-lg font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="terms-edit-submit">
                  {saving ? 'Submitting…' : 'Send to VP for Approval'}
                </button>
              </>
            );
          })()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// -------------------- Sub-modals --------------------

const PaymentModal = ({ leadId, stage, schedule, modes, onClose, onSaved }) => {
  const [form, setForm] = useState({
    stage_id: stage?.id || '',
    amount: stage?.amount || '',
    mode: modes[0] || 'Online Transfer',
    reference: '',
    received_at: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr(''); setSaving(true);
    try {
      const amt = Number(form.amount);
      if (!Number.isFinite(amt) || amt <= 0) {
        setErr('Amount must be a positive number');
        setSaving(false);
        return;
      }
      await axios.post(`${API}/leads/${leadId}/payments`, {
        ...form,
        stage_id: form.stage_id || null,
        amount: amt,
      });
      await onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Record Payment Received</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-[#6D8E87] mb-1">Stage (optional)</label>
            <select value={form.stage_id} onChange={e => setForm({ ...form, stage_id: e.target.value })}
              className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" data-testid="pay-stage">
              <option value="">— Not linked to a stage —</option>
              {(schedule || []).filter(s => s.status !== 'received').map(s =>
                <option key={s.id} value={s.id}>{s.label} · {money(s.amount)}</option>
              )}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#6D8E87] mb-1">Amount *</label>
              <input type="number" step="any" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" required data-testid="pay-amount" />
            </div>
            <div>
              <label className="block text-xs text-[#6D8E87] mb-1">Mode *</label>
              <select value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" data-testid="pay-mode">
                {modes.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#6D8E87] mb-1">Reference (UTR / Cheque no)</label>
              <input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })}
                placeholder="e.g. UTR12345"
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" data-testid="pay-reference" />
            </div>
            <div>
              <label className="block text-xs text-[#6D8E87] mb-1">Received Date *</label>
              <input type="date" value={form.received_at} onChange={e => setForm({ ...form, received_at: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" required data-testid="pay-received-at" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#6D8E87] mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={2} className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" data-testid="pay-notes" />
          </div>
          {err && <div className="p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-700" data-testid="pay-error">{err}</div>}
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-2 border border-[#D5E8E4] rounded-lg text-sm">Cancel</button>
            <button onClick={submit} disabled={saving || !form.amount || !form.mode || !form.received_at}
              className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm disabled:opacity-50" data-testid="save-payment-btn">
              {saving ? 'Saving…' : 'Save Payment'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DeferModal = ({ leadId, stage, onClose, onSaved }) => {
  const [newDate, setNewDate] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setErr(''); setSaving(true);
    try {
      await axios.post(`${API}/leads/${leadId}/schedule-stages/${stage.id}/defer`, { new_due_date: newDate, reason });
      await onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to defer');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Defer &ldquo;{stage?.label}&rdquo;</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-[#6D8E87] mb-1">New Expected Date *</label>
            <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
              className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" required data-testid="defer-new-date" />
          </div>
          <div>
            <label className="block text-xs text-[#6D8E87] mb-1">Reason *</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
              placeholder="Explain why this payment stage is being pushed out."
              className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg" required data-testid="defer-reason" />
          </div>
          {err && <div className="p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-700" data-testid="defer-error">{err}</div>}
          <p className="text-[11px] text-[#6D8E87]">Deferred stages remain in the aging calc — they still show as outstanding if the new date is more than 90 days past.</p>
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-2 border border-[#D5E8E4] rounded-lg text-sm">Cancel</button>
            <button onClick={submit} disabled={saving || !newDate || !reason}
              className="flex-1 py-2 bg-amber-600 text-white rounded-lg text-sm disabled:opacity-50" data-testid="save-defer-btn">
              {saving ? 'Saving…' : 'Confirm Deferral'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ScheduleEditModal = ({ leadId, initialStages, billedValue, onClose, onSaved }) => {
  const [rows, setRows] = useState(initialStages.map(s => ({
    label: s.label, percent: s.percent, due_after_days: s.due_after_days || 0,
  })));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const totalPct = rows.reduce((s, r) => s + (parseFloat(r.percent) || 0), 0);
  const submit = async () => {
    setErr(''); setSaving(true);
    try {
      await axios.put(`${API}/leads/${leadId}/payment-schedule`, { stages: rows });
      await onSaved();
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Edit Payment Schedule</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <p className="text-xs text-[#6D8E87]">Billed value: <strong>{money(billedValue)}</strong>. Stage percentages must sum to 100.</p>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <input value={r.label} onChange={e => setRows(rows.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))}
                placeholder="Label" className="col-span-5 px-2 py-1 border border-[#D5E8E4] rounded" data-testid={`sched-label-${i}`} />
              <input type="number" step="any" value={r.percent} onChange={e => setRows(rows.map((x, idx) => idx === i ? { ...x, percent: e.target.value } : x))}
                placeholder="%" className="col-span-2 px-2 py-1 border border-[#D5E8E4] rounded" data-testid={`sched-pct-${i}`} />
              <input type="number" value={r.due_after_days} onChange={e => setRows(rows.map((x, idx) => idx === i ? { ...x, due_after_days: e.target.value } : x))}
                placeholder="Days after Won" className="col-span-3 px-2 py-1 border border-[#D5E8E4] rounded" data-testid={`sched-days-${i}`} />
              <button onClick={() => setRows(rows.filter((_, idx) => idx !== i))} className="col-span-2 text-rose-500 hover:text-rose-700 text-xs" data-testid={`sched-del-${i}`}>Remove</button>
            </div>
          ))}
          <button onClick={() => setRows([...rows, { label: '', percent: 0, due_after_days: 0 }])}
            className="text-xs text-[#00a69c] hover:text-[#008f86]" data-testid="sched-add-row">+ Add stage</button>
          <p className={`text-xs font-medium ${Math.abs(totalPct - 100) > 0.5 ? 'text-rose-700' : 'text-emerald-700'}`} data-testid="sched-total-pct">
            Total: {totalPct.toFixed(2)}% {Math.abs(totalPct - 100) <= 0.5 ? '✓' : `(needs to equal 100)`}
          </p>
          {err && <div className="p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-700" data-testid="sched-error">{err}</div>}
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-2 border border-[#D5E8E4] rounded-lg text-sm">Cancel</button>
            <button onClick={submit} disabled={saving || Math.abs(totalPct - 100) > 0.5}
              className="flex-1 py-2 bg-[#00a69c] text-white rounded-lg text-sm disabled:opacity-50" data-testid="save-schedule-btn">
              {saving ? 'Saving…' : 'Save Schedule'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
